/**
 * DeltaBuffer - Pure JS class for buffering streaming text deltas.
 * Buffers deltas by sequence index, deduplicates, flushes in strict sequential order.
 * Two modes: sequenced (0-based index) and append (legacy, no index).
 *
 * - 30s inactivity timeout: auto-flush everything if no new delta arrives
 * - 3s grace period after markFin(): wait for stragglers, then final flush
 */
export class DeltaBuffer {
  constructor({ onFlush, inactivityTimeout = 30000, graceMs = 3000 } = {}) {
    this.onFlush = typeof onFlush === 'function' ? onFlush : () => {};
    this.inactivityTimeout = inactivityTimeout;
    this.graceMs = graceMs;

    this.buffer = new Map(); // index -> content
    this.nextExpectedIndex = 0;
    this.flushedContent = '';
    this.isFin = false;
    this.graceExpired = false; // true after grace timer fires
    this.inactivityTimer = null;
    this.graceTimer = null;
  }

  /**
   * Add a delta. Deduplicates by index.
   * Accepts stragglers during grace period after markFin().
   * @param {number} index - 0-based delta index (use for ordering)
   * @param {string} content - Text content
   * @param {number} [sequenceId] - Optional sequence (not used for ordering)
   */
  push(index, content, sequenceId) {
    if (this.graceExpired) return;

    this._resetInactivityTimer();

    const effectiveIndex = typeof index === 'number' ? index : this.buffer.size;
    const text = typeof content === 'string' ? content : String(content ?? '');

    if (text === '') return;

    this.buffer.set(effectiveIndex, text);
    this._flushContiguous();
  }

  /**
   * Signal end-of-stream. Starts grace period, then final flush.
   * @param {number|null} expectedCount - Expected total chunks (null = unknown)
   */
  markFin(expectedCount) {
    if (this.isFin) return;
    this.isFin = true;
    this._clearInactivityTimer();

    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
    }

    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      this.graceExpired = true;
      this._flushAll();
    }, this.graceMs);
  }

  /**
   * Get current buffered content (not yet flushed).
   */
  getContent() {
    const sortedIndices = Array.from(this.buffer.keys()).sort((a, b) => a - b);
    return sortedIndices.map((idx) => this.buffer.get(idx)).join('');
  }

  /**
   * Get full content (flushed + buffered).
   */
  getFullContent() {
    return this.flushedContent + this.getContent();
  }

  /**
   * Get next append index for legacy/append mode (no explicit index).
   * Uses nextExpectedIndex + buffer.size so indices stay correct after _flushContiguous
   * consumes entries (buffer.size would be 0 but nextExpectedIndex tracks consumed count).
   */
  getNextAppendIndex() {
    return this.nextExpectedIndex + this.buffer.size;
  }

  /**
   * Clean up timers.
   */
  destroy() {
    this._clearInactivityTimer();
    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
    this.graceExpired = true;
    this.buffer.clear();
    this.isFin = true;
  }

  _resetInactivityTimer() {
    this._clearInactivityTimer();
    this.inactivityTimer = setTimeout(() => {
      this.inactivityTimer = null;
      this._flushAll();
    }, this.inactivityTimeout);
  }

  _clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  _flushContiguous() {
    while (this.buffer.has(this.nextExpectedIndex)) {
      const text = this.buffer.get(this.nextExpectedIndex);
      this.buffer.delete(this.nextExpectedIndex);
      this.flushedContent += text;
      this.nextExpectedIndex += 1;
      this.onFlush(this.flushedContent);
    }
  }

  _flushAll() {
    const sortedIndices = Array.from(this.buffer.keys()).sort((a, b) => a - b);
    for (const idx of sortedIndices) {
      const text = this.buffer.get(idx);
      this.flushedContent += text;
    }
    this.buffer.clear();
    this.nextExpectedIndex = 0;
    if (sortedIndices.length > 0) {
      this.onFlush(this.flushedContent);
    }
  }
}

export default DeltaBuffer;
